import api from '@/lib/api';

export interface WarehouseContact {
  label?: string;
  /** Native Interne Konversation hedefi (yeni sistem) */
  org_id?: string;
  /** Eski email tabanlı sistemden kalan alan (artık kullanılmıyor) */
  email?: string;
}

export interface Organization {
  id: string;
  name: string;
  domain?: string | null;
  support_email?: string | null;
  email_config?: any;
  is_active?: boolean;
  is_warehouse?: boolean;
  warehouse_contacts?: WarehouseContact[] | null;
}

export interface OrganizationsResponse {
  success: boolean;
  data: Organization[];
}

export interface CreateOrganizationData {
  name: string;
  domain?: string | null;
  support_email?: string | null;
  email_config?: any;
  is_warehouse?: boolean;
  warehouse_contacts?: WarehouseContact[] | null;
}

export interface UpdateOrganizationData extends CreateOrganizationData {
  is_active?: boolean;
}

export interface IMAPTestParams {
  host: string;
  port?: number;
  secure?: boolean;
  user: string;
  password: string;
}

export interface SMTPTestParams {
  host: string;
  port?: number;
  secure?: boolean;
  user: string;
  password: string;
}

export interface IMAPTestResult {
  success: boolean;
  connected?: boolean;
  authenticated?: boolean;
  inboxAccessible?: boolean;
  emailCount?: number;
  duration?: string;
  timestamp?: string;
  error?: string;
  errorCode?: string;
  details?: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
  };
  organizationName?: string;
}

export interface SMTPTestResult {
  success: boolean;
  connected?: boolean;
  authenticated?: boolean;
  duration?: string;
  timestamp?: string;
  error?: string;
  errorCode?: string;
  details?: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
  };
  organizationName?: string;
}

class OrganizationsService {
  async getOrganizations(): Promise<OrganizationsResponse> {
    const response = await api.get<OrganizationsResponse>('/organizations');
    return response.data;
  }

  async createOrganization(data: CreateOrganizationData): Promise<Organization> {
    const response = await api.post<{ success: boolean; data: Organization }>('/organizations', data);
    return response.data.data;
  }

  async updateOrganization(id: string, data: UpdateOrganizationData): Promise<Organization> {
    const response = await api.put<{ success: boolean; data: Organization }>(`/organizations/${id}`, data);
    return response.data.data;
  }

  async deleteOrganization(id: string): Promise<Organization> {
    const response = await api.delete<{ success: boolean; data: Organization }>(`/organizations/${id}`);
    return response.data.data;
  }

  async testIMAPConnection(params: IMAPTestParams): Promise<IMAPTestResult> {
    // Backend imap-test-simple route: POST /api/imap-test/test-connection
    const response = await api.post<IMAPTestResult>('/imap-test/test-connection', params);
    return response.data;
  }

  async testOrganizationIMAP(org: Organization & { email_config?: any }): Promise<IMAPTestResult> {
    const imap = org?.email_config?.imap || {};
    const params: IMAPTestParams = {
      host: imap.host,
      port: imap.port ?? 993,
      secure: imap.secure ?? true,
      user: imap.user,
      password: imap.password, // not returned by secured listing; must be provided in UI when testing
    } as IMAPTestParams;
    const result = await this.testIMAPConnection(params);
    return { ...result, organizationName: org.name };
  }

  async testSMTPConnection(params: SMTPTestParams): Promise<SMTPTestResult> {
    // Backend smtp-test route: POST /api/smtp-test/test-connection
    const response = await api.post<SMTPTestResult>('/smtp-test/test-connection', params);
    return response.data;
  }

  async testOrganizationSMTP(org: Organization & { email_config?: any }): Promise<SMTPTestResult> {
    const smtp = org?.email_config?.smtp || {};
    const params: SMTPTestParams = {
      host: smtp.host,
      port: smtp.port ?? 587,
      secure: smtp.secure ?? false,
      user: smtp.user,
      password: smtp.password,
    } as SMTPTestParams;
    const result = await this.testSMTPConnection(params);
    return { ...result, organizationName: org.name };
  }
}

const organizationsService = new OrganizationsService();
export default organizationsService;

// Named exports for hooks expecting functions
export const getOrganizations = () => organizationsService.getOrganizations();
export const createOrganization = (data: CreateOrganizationData) => organizationsService.createOrganization(data);
export const updateOrganization = (id: string, data: UpdateOrganizationData) => organizationsService.updateOrganization(id, data);
export const deleteOrganization = (id: string) => organizationsService.deleteOrganization(id);
export const testIMAPConnection = (params: IMAPTestParams) => organizationsService.testIMAPConnection(params);
export const testOrganizationIMAP = (org: Organization & { email_config?: any }) => organizationsService.testOrganizationIMAP(org);
export const testSMTPConnection = (params: SMTPTestParams) => organizationsService.testSMTPConnection(params);
export const testOrganizationSMTP = (org: Organization & { email_config?: any }) => organizationsService.testOrganizationSMTP(org);
